import * as PropTypes from 'prop-types';
import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { STAFF_TYPES } from '@medflex/utils/constants';
import Button from '../Button';
import DropDown from '../DropDown';
import InputCheckbox from '../InputCheckbox';
import InputField from '../InputField';
import FormErrorMessage from '../FormErrorMessage';

const StaffMemberEdit = ({
  onSubmit,
  state = 'composing',
  validationSchema,
  initialValues,
  isCreating,
  onCheckBoxAction,
}) => {
  const {
    register: registerFormElement,
    errors,
    watch,
    handleSubmit: wrapSubmit,
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onTouched',
  });
  const { t } = useTranslation('components');
  const data = watch();
  const getState = name => {
    if (!errors[name] && data && data[name]) return 'valid';
    if (errors[name]) return 'invalid';
    return 'default';
  };

  return (
    <form
      data-component="StaffMemberEdit"
      className="flex flex-col max-h-full"
      onSubmit={wrapSubmit(onSubmit)}
    >
      <div className="flex-1">
        <fieldset className="mb-12">
          <div className="mb-6">
            <label className="block mb-1" htmlFor="StaffMemberEdit.jobTitle">
              <span className="font-semibold mr-1">{t('staff_member_form.jobTitle.label')}</span>
              {t('staff_member_form.jobTitle.info')}
            </label>
            <InputField
              name="jobTitle"
              id="StaffMemberEdit.jobTitle"
              type="jobTitle"
              state={getState('jobTitle')}
              readOnly={state === 'loading'}
              defaultValue={initialValues && initialValues.jobTitle}
              placeholder={
                initialValues.type === STAFF_TYPES.PHYSICIAN
                  ? t('staff_member_form.jobTitle.placeholder_physician')
                  : t('staff_member_form.jobTitle.placeholder_assistant')
              }
              ref={registerFormElement}
            />
            {errors.jobTitle && <FormErrorMessage message={errors.jobTitle.message} />}
          </div>
          {initialValues.type === STAFF_TYPES.PHYSICIAN && (
            <div className="mb-6">
              <label className="block mb-1 font-semibold" htmlFor="StaffMemberEdit.title">
                {t('staff_member_form.name_title.label')}
              </label>
              <DropDown
                name="title"
                state={getState('title')}
                id="StaffMemberEdit.title"
                defaultValue={initialValues && initialValues.title}
                options={t('staff_member_form.name_title.titles', { returnObjects: true })}
                ref={registerFormElement}
              />
              {errors.title && <FormErrorMessage message={errors.title.message} />}
            </div>
          )}
          <div className="mb-6">
            <label className="block mb-1 font-semibold" htmlFor="StaffMemberEdit.firstName">
              {t('staff_member_form.first_name.label')}
            </label>
            <InputField
              name="firstName"
              id="StaffMemberEdit.firstName"
              state={getState('firstName')}
              readOnly={state === 'loading'}
              defaultValue={initialValues && initialValues.firstName}
              placeholder={t('staff_member_form.first_name.placeholder')}
              ref={registerFormElement}
            />
            {errors.firstName && <FormErrorMessage message={errors.firstName.message} />}
          </div>
          <div className="mb-6">
            <label className="block mb-1 font-semibold" htmlFor="StaffMemberEdit.lastName">
              {t('staff_member_form.last_name.label')}
            </label>
            <InputField
              name="lastName"
              id="StaffMemberEdit.lastName"
              state={getState('lastName')}
              readOnly={state === 'loading'}
              defaultValue={initialValues && initialValues.lastName}
              placeholder={t('staff_member_form.last_name.placeholder')}
              ref={registerFormElement}
            />
            {errors.lastName && <FormErrorMessage message={errors.lastName.message} />}
          </div>
          <div className="mb-12">
            <label className="block mb-1" htmlFor="StaffMemberEdit.email">
              <span className="font-semibold mr-1">{t('staff_member_form.email.label')}</span>
              {t('staff_member_form.email.info')}
            </label>
            <InputField
              className="mb-1"
              name="email"
              id="StaffMemberEdit.email"
              type="email"
              state={getState('email')}
              readOnly={state === 'loading' || !!initialValues.email}
              defaultValue={initialValues && initialValues.email}
              placeholder={t('staff_member_form.email.placeholder')}
              ref={registerFormElement}
              inputClassName={
                initialValues && initialValues.email
                  ? 'border-slate-300 cursor-not-allowed pointer-events-none cursor-not-allowed'
                  : ''
              }
            />
            {isCreating && <p className="text-slate-500">{t('staff_member_form.submitInfo')}</p>}
            {errors.email && <FormErrorMessage message={errors.email.message} />}
          </div>
          <div>
            <div className="font-semibold mb-4">{t('staff_member_form.authorizations.title')}</div>
            <label className="mb-6 flex items-center" htmlFor="patientAccessGranted">
              <InputCheckbox
                name="patientAccessGranted"
                id="patientAccessGranted"
                className="flex-none h-7 w-7"
                defaultChecked={initialValues && initialValues.patientAccessGranted}
                state={getState('patientAccessGranted')}
                ref={registerFormElement}
                onChange={onCheckBoxAction}
              />
              <div className="ml-4">
                <Trans t={t} i18nKey="staff_member_form.authorizations.patientAccess">
                  <strong>Patienten in Kontaktliste freigeben</strong>
                </Trans>
              </div>
            </label>
            {errors.patientAccessGranted && (
              <FormErrorMessage message={errors.patientAccessGranted.message} />
            )}

            <label className="flex items-center" htmlFor="physicianAccessGranted">
              <InputCheckbox
                name="physicianAccessGranted"
                id="physicianAccessGranted"
                className="flex-none h-7 w-7"
                defaultChecked={initialValues && initialValues.physicianAccessGranted}
                state={getState('physicianAccessGranted')}
                ref={registerFormElement}
                onChange={onCheckBoxAction}
              />
              <div className="ml-4">
                <Trans t={t} i18nKey="staff_member_form.authorizations.physicianAccess">
                  <strong>Kollegen in Kontaktliste freigeben</strong>
                </Trans>
              </div>
            </label>
            {errors.physicianAccessGranted && (
              <FormErrorMessage message={errors.physicianAccessGranted.message} />
            )}
          </div>
        </fieldset>

        <div className="mb-4">
          <Button
            className="flex-none w-full"
            isDisabled={state === 'loading'}
            isLoading={state === 'loading'}
            type="submit"
          >
            {t('staff_member_form.submit')}
          </Button>
        </div>
      </div>
    </form>
  );
};

StaffMemberEdit.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  state: PropTypes.oneOf(['composing', 'loading']),
  isCreating: PropTypes.bool,
  initialValues: PropTypes.shape({
    title: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    jobTitle: PropTypes.string,
    patientAccessGranted: PropTypes.bool,
    physicianAccessGranted: PropTypes.bool,
    type: PropTypes.oneOf([STAFF_TYPES.ASSISTANT, STAFF_TYPES.PHYSICIAN]),
  }),
  validationSchema: PropTypes.shape({
    title: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    jobTitle: PropTypes.string,
    email: PropTypes.string,
    patientAccessGranted: PropTypes.bool,
    physicianAccessGranted: PropTypes.bool,
  }),
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.array,
    PropTypes.object,
  ]),
  onCheckBoxAction: PropTypes.func,
};

StaffMemberEdit.defaultProps = {
  isCreating: false,
  onCheckBoxAction: () => {},
};

export default StaffMemberEdit;
